<template>
  <section class="sketch-plan-workspace">
    <div class="sketch-plan-main">
      <aside class="sketch-plan-settings">
        <section class="setting-section">
          <header class="section-title">
            <span>01</span>
            <div><strong>上传手绘线稿</strong><small>支持纸面草图、平板手绘或结构线框图</small></div>
          </header>

          <label class="sketch-dropzone" :class="{ filled: previewUrl }">
            <input type="file" accept="image/png,image/jpeg,image/webp" @change="handleFile" />
            <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" alt="待生成方案的手绘线稿" decoding="async" />
            <span v-else class="dropzone-empty">
              <i class="el-icon-upload2"></i>
              <strong>选择线稿图片</strong>
              <small>PNG / JPG / WebP，单张不超过 10MB</small>
            </span>
            <button v-if="previewUrl" type="button" class="replace-sketch">更换</button>
          </label>

          <div class="file-rule" :class="{ valid: previewUrl }">
            <i :class="previewUrl ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
            <span>{{ previewUrl ? (sketchFile ? sketchFile.name : '历史任务线稿') : '线条越清楚，生成方案越接近原始结构' }}</span>
          </div>
        </section>

        <section class="setting-section requirement-section">
          <header class="section-title">
            <span>02</span>
            <div><strong>填写生成要求</strong><small>请说明线稿要生成什么，以及材质、颜色和效果</small></div>
          </header>

          <label class="requirement-field">
            <span>提示词 <em>{{ requirement.length }}/500</em></span>
            <textarea
              v-model.trim="requirement"
              maxlength="500"
              placeholder="例如：按照线稿生成一樘现代铜门，暖棕铜色拉丝材质，搭配简洁五金，输出写实白底效果图。"
            ></textarea>
            <AiPromptOptimizer
              v-model="requirement"
              :disabled="generating"
              :max-length="500"
              context="线稿生成写实效果图的提示词"
            />
          </label>
        </section>

        <section class="setting-section">
          <header class="section-title">
            <span>03</span>
            <div><strong>生成质量</strong><small>质量与输出尺寸分别控制</small></div>
          </header>
          <QualitySelector v-model="selectedQuality" :disabled="generating" :show-label="false" />
        </section>

        <button class="generate-button" :disabled="generating" @click="generatePlan">
          <span v-if="generating" class="button-spinner"></span>
          <i v-else class="el-icon-magic-stick"></i>
          <span>{{ generating ? generationText : '直接生成效果图' }}</span>
          <small v-if="!generating">上传线稿并填写生成要求后开始生成</small>
        </button>
        <div v-if="generating" class="generation-progress" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
          <i :style="{ width: `${progress}%` }"></i>
        </div>
      </aside>

      <section class="result-panel">
        <header class="result-head">
          <div>
            <strong>线稿与效果图对照</strong>
            <span>生成后重点核对结构、轮廓、比例和材质是否与线稿一致</span>
          </div>
          <div class="result-actions">
            <WorkflowTuningButton
              v-if="resultUrl"
              variant="button"
              icon-only
              :image-url="resultUrl"
              :prompt="finalPrompt"
              source-model="gpt-image-2-official"
              module-name="线稿出方案"
            />
            <button v-if="resultUrl" type="button" title="下载产品方案" aria-label="下载产品方案" @click="downloadResult">
              <i class="el-icon-download"></i>
            </button>
            <button v-if="resultUrl" type="button" title="重新生成" aria-label="重新生成" :disabled="generating" @click="generatePlan">
              <i class="el-icon-refresh"></i>
            </button>
          </div>
        </header>

        <div class="result-stage">
          <article class="result-viewport source-viewport">
            <span class="viewport-label">A · 上传线稿</span>
            <button v-if="previewUrl" type="button" class="image-preview-button" @click="previewImage(previewUrl, '上传线稿')">
              <img :src="$ossImageUrl(previewUrl, 'preview')" alt="用户上传的产品手绘线稿" decoding="async" />
              <i class="el-icon-zoom-in"></i>
            </button>
            <div v-else class="viewport-empty">
              <i class="el-icon-picture-outline"></i>
              <strong>等待上传线稿</strong>
              <span>请先在左侧选择一张线稿图片</span>
            </div>
          </article>

          <article class="result-viewport plan-viewport">
            <span class="viewport-label">B · {{ resultUrl ? '生成方案' : '等待生成' }}</span>
            <button
              v-if="resultUrl"
              type="button"
              class="image-preview-button"
              @click="previewImage(resultUrl, '生成产品方案')"
            >
              <img :src="$ossImageUrl(resultUrl, 'preview')" alt="AI 根据线稿生成的产品方案" decoding="async" />
              <i class="el-icon-zoom-in"></i>
            </button>
            <div v-else-if="generating" class="viewport-empty model-loading">
              <span class="model-loader"><i></i><i></i><i></i></span>
              <strong>{{ generationText }}</strong>
              <span>正在识别线稿结构并构建产品材质</span>
            </div>
            <div v-else class="viewport-empty">
              <i class="el-icon-magic-stick"></i>
              <strong>等待生成方案</strong>
              <span>上传线稿后即可直接生成效果图</span>
            </div>
          </article>
        </div>

        <div class="quality-strip">
          <span><i class="el-icon-check"></i> 保留线稿轮廓</span>
          <span><i class="el-icon-check"></i> 保持产品比例</span>
          <span><i class="el-icon-check"></i> 按提示词生成</span>
          <span><i class="el-icon-check"></i> 单张完整输出</span>
        </div>

        <div v-if="errorMessage" class="generation-error">
          <i class="el-icon-warning-outline"></i>
          <div><strong>{{ errorMessage }}</strong><span>{{ errorSuggestion }}</span></div>
          <button v-if="currentTaskIds.length && errorRetryable" type="button" :disabled="generating" @click="continuePolling">继续查询</button>
        </div>
      </section>
    </div>

    <section class="method-panel">
      <header>
        <div>
          <span class="workspace-kicker">AI PROMPTS</span>
          <h2>提交给 AI 的完整提示词</h2>
          <p>以下内容会随生成要求实时更新，提交时会与线稿图片一起发送给模型。</p>
        </div>
      </header>
      <div class="prompt-content">
        <div><span>SYSTEM PROMPT</span><strong>系统规则</strong></div>
        <pre>{{ systemPrompt || '正在加载系统提示词…' }}</pre>
      </div>
      <div class="prompt-content">
        <div><span>FINAL USER PROMPT</span><strong>本次最终提示词</strong></div>
        <pre>{{ finalPrompt }}</pre>
      </div>
    </section>

    <section class="history-panel">
      <header class="history-head">
        <div><span class="workspace-kicker">GENERATION RECORDS</span><h2>线稿出方案记录</h2><p>保存已提交的线稿、生成要求与产品方案，方便继续查看和下载。</p></div>
        <button type="button" class="history-refresh" :disabled="historyLoading" @click="loadPendingTask">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>{{ historyLoading ? '正在刷新' : '刷新记录' }}
        </button>
      </header>

      <div v-if="history.length" class="history-grid">
        <article v-for="record in history" :key="record.id" :class="{ active: currentBatchId === record.id }">
          <div class="history-images">
            <span class="history-status" :class="record.status">{{ statusText(record.status) }}</span>
            <button v-if="record.source" type="button" @click="previewHistoryImage(record, 'source')">
              <img :src="$ossImageUrl(record.source, 'thumbnail')" alt="历史任务手绘线稿" loading="lazy" decoding="async" /><small>线稿</small>
            </button>
            <div v-else><i class="el-icon-picture-outline"></i><small>线稿不可用</small></div>
            <button v-if="record.result" type="button" @click="previewHistoryImage(record, 'result')">
              <img :src="$ossImageUrl(record.result, 'thumbnail')" alt="历史任务生成的产品方案" loading="lazy" decoding="async" /><small>产品方案</small>
            </button>
            <div v-else><i :class="record.status === 'generating' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i><small>{{ statusText(record.status) }}</small></div>
          </div>
          <ImageDimensionsRow :items="[{ label: '线稿尺寸', url: record.source, probe: true }, { label: '结果尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight }]" />
          <GenerationModelDetails :record="record" />
          <GenerationFailureDetails :record="record" />
          <footer>
            <div><strong>{{ record.style || '线稿产品方案' }}</strong><small>{{ record.size || '3:4' }} · {{ formatRecordTime(record.createdAt) }}</small></div>
            <div class="history-actions">
              <WorkflowTuningButton
                v-if="record.result"
                variant="button"
                icon-only
                :image-url="record.result"
                :prompt="record.prompt || finalPrompt"
                :source-model="record.model || ''"
                module-name="线稿出方案"
              />
              <button type="button" title="恢复查看" aria-label="恢复查看这条记录" @click="restoreHistoryRecord(record)"><i class="el-icon-view"></i></button>
              <button v-if="record.result" type="button" title="下载方案" aria-label="下载这张产品方案" @click="downloadHistoryResult(record)"><i class="el-icon-download"></i></button>
            </div>
          </footer>
        </article>
      </div>
      <div v-else class="history-empty">
        <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i>
        <strong>{{ historyLoading ? '正在加载生成记录' : '还没有线稿出方案记录' }}</strong>
        <span>上传第一张线稿并生成后，任务与结果会自动保存在这里。</span>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 草图方案生成把空间描述、参考图和比例提交为异步批次，完成状态由公共记录轮询处理。 */
import { apiRequest, downloadResource } from '@/services/api'
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION } from '@/utils/outputQuality'
import { closestImageRatio } from '@/utils/imageAspectRatio'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import QualitySelector from '@/components/QualitySelector.vue'

const STAGE = 'sketch_plan'

export default {
  name: 'SketchPlanCreation',
  components: { ImageDimensionsRow, AiPromptOptimizer, QualitySelector },
  data() {
    return {
      sketchFile: null,
      previewUrl: '',
      requirement: '',
      selectedRatio: '3:4',
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      apiConfigured: false,
      generating: false,
      progress: 0,
      generationText: '准备生成产品方案',
      resultUrl: '',
      errorMessage: '',
      errorSuggestion: '',
      errorRetryable: false,
      currentBatchId: '',
      currentTaskIds: [],
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      systemPrompt: ''
    }
  },
  computed: {
    finalPrompt() {
      const requirement = this.requirement.trim() || '自动识别线稿内容，使用协调、真实、可落地的材质、配色、灯光和背景完成设计。'
      return `【线稿直接生成效果图】
输入线稿是最终结构、构图、轮廓和比例的唯一视觉依据。先判断线稿表现的是室内空间、建筑、家具还是产品，再严格保留可见主体、透视关系、空间结构、门窗开口、家具位置、外轮廓、宽高比例、分格与关键结构线，不得忽略线稿另起构图。
用户补充要求：${requirement}
输出要求：把线稿完整转换为一张写实、清晰、可落地的高品质效果图。自动补充合理的材质、颜色、光影和环境细节，但不得改变原线稿确定的结构和布局；画面比例与输入保持一致，不裁切、不拉伸、不压缩。
最终图片中不得保留草图线、尺寸标注、解释文字、Logo、水印、边框或拼图。只输出一张最终效果图。`
    }
  },
  created() {
    this.loadConfiguration()
    this.loadPendingTask()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const health = await this.request('/api/health')
        this.apiConfigured = Boolean(health.configured)
      } catch (error) {
        this.apiConfigured = false
      }
      try {
        const promptProfile = await this.request('/api/prompts/sketch-plan')
        this.systemPrompt = promptProfile.systemPrompt || ''
      } catch (error) {
        this.systemPrompt = '系统提示词暂时无法加载，请刷新页面后重试。'
      }
    },
    handleFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.showMessage('仅支持 PNG、JPG 或 WebP 线稿图片', 'warning')
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showMessage('线稿图片必须小于 10MB，请压缩后再上传', 'warning')
        return
      }
      const localPreview = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.sketchFile = file
        this.previewUrl = localPreview
        this.resultUrl = ''
        this.errorMessage = ''
        this.errorSuggestion = ''
        this.currentBatchId = ''
        this.currentTaskIds = []
        this.selectedRatio = closestImageRatio(image.naturalWidth, image.naturalHeight)
      }
      image.onerror = () => {
        URL.revokeObjectURL(localPreview)
        this.showMessage('线稿文件已损坏或无法读取，请重新选择', 'error')
      }
      image.src = localPreview
    },
    validateForm() {
      if (!this.sketchFile) throw new Error('请先上传一张手绘线稿')
      if (!this.requirement.trim()) throw new Error('请填写生成要求，说明线稿需要生成什么效果图')
    },
    async uploadSketch() {
      const form = new FormData()
      form.append('file', this.sketchFile)
      return this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    async pollTask(taskId) {
      const startedAt = Date.now()
      let queryFailures = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        try {
          const task = await this.request(`/api/generations/${encodeURIComponent(taskId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '方案任务状态查询超时'
          })
          queryFailures = 0
          this.progress = Math.max(this.progress, Math.min(96, Number(task.progress || 0)))
          if (task.status === 'completed') {
            const image = this.taskImage(task)
            if (!image) throw Object.assign(new Error('任务已完成，但未返回可用的产品方案'), { retryable: false })
            return image
          }
          if (task.status === 'failed') {
            throw Object.assign(new Error(task.error && task.error.message ? task.error.message : 'AI 产品方案生成失败'), task.error || {}, { terminal: true })
          }
          this.generationText = this.progress > 48 ? `正在校验产品细节 ${this.progress}%` : `正在构建产品方案 ${this.progress}%`
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          queryFailures += 1
          this.generationText = `网络波动，正在恢复任务进度（${queryFailures}/5）`
          if (queryFailures >= 5) {
            throw Object.assign(new Error('暂时无法查询生成进度，但任务仍可能在后台处理'), {
              suggestion: '请恢复网络后点击“继续查询”，无需重复创建任务。',
              retryable: true,
              pending: true
            })
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      throw Object.assign(new Error('方案生成时间较长，任务仍在后台处理中'), {
        suggestion: '请稍后点击“继续查询”，无需重复创建任务。',
        retryable: true,
        pending: true
      })
    },
    async generatePlan() {
      if (this.generating) return
      try {
        this.validateForm()
      } catch (error) {
        this.showMessage(error.message, 'warning')
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }

      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.resultUrl = ''
      this.progress = 4
      this.generationText = '正在上传线稿'
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      const requestId = `sketch-plan-${random}`.slice(0, 64)
      const pendingId = `interior-${requestId}`
      const createdAt = new Date().toISOString()
      this.savePendingTask(pendingId, createdAt)
      let requestStarted = false
      try {
        const asset = await this.uploadSketch()
        this.savePendingTask(pendingId, createdAt, asset.localUrl)
        this.progress = 12
        this.generationText = '正在创建产品方案任务'
        requestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url],
            prompt: this.finalPrompt,
            space: '线稿效果图',
            style: this.requirement.trim().slice(0, 60) || '自动识别并生成效果图',
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: STAGE,
            floorPlanRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: [],
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建方案任务等待超时，正在确认是否已成功提交'
        })
        const task = (batch.tasks || []).find(item => item && item.id)
        this.currentBatchId = batch.batchId || pendingId
        if (!task) {
          throw Object.assign(new Error('任务已保存，正在后台确认生成任务'), {
            suggestion: '系统会自动刷新任务记录，无需重复提交。',
            retryable: true,
            pending: true
          })
        }
        this.currentTaskIds = [task.id]
        removePendingGeneration(pendingId)
        this.progress = 18
        this.resultUrl = await this.pollTask(task.id)
        this.progress = 100
        this.generationText = '产品方案已生成'
        this.showMessage('产品方案已生成，请核对结构、材质和背景', 'success')
      } catch (error) {
        this.errorMessage = error.message || '产品方案生成失败，请稍后重试'
        this.errorSuggestion = error.suggestion || '请检查线稿、生成要求与网络后重试。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (requestStarted && (error.retryable || error.pending)) this.schedulePendingTaskRefresh()
        else if (!this.currentTaskIds.length) removePendingGeneration(pendingId)
        this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    savePendingTask(id, createdAt, localUrl = '') {
      savePendingGeneration({
        id,
        space: '产品线稿方案',
        style: this.requirement.trim().slice(0, 60) || '线稿产品方案',
        size: this.selectedRatio,
        prompt: this.finalPrompt,
        status: 'generating',
        stage: STAGE,
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: {
          floorPlanImages: localUrl ? [{ type: 'original', label: '手绘线稿', url: localUrl }] : [],
          referenceImages: [],
          annotation: null
        },
        createdAt
      })
    },
    async loadPendingTask() {
      if (this.historyLoading) return
      this.historyLoading = true
      try {
        const response = await this.request(`/api/records/paged?stage=${STAGE}&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const merged = (this.historyPage === 1 ? mergePendingGenerations(records, STAGE) : records).filter(record => record.stage === STAGE)
        this.history = merged.slice(0, 12).map(record => ({
          id: record.id,
          style: record.style,
          size: record.size,
          prompt: record.prompt || '',
          status: record.status,
          createdAt: record.createdAt,
          source: record.inputContext && record.inputContext.floorPlanImages && record.inputContext.floorPlanImages[0]
            ? record.inputContext.floorPlanImages[0].url
            : '',
          result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : '',
          resultWidth: record.images && record.images[0] ? record.images[0].width : null,
          resultHeight: record.images && record.images[0] ? record.images[0].height : null,
          taskIds: (record.tasks || []).map(task => task && task.id).filter(Boolean),
          taskStates: record.tasks || [],
          errorMessage: record.error || record.errorMessage || ''
          , imageModel: record.imageModel || ''
        }))
        const active = merged.find(record => record.status === 'generating')
        if (!active) return
        const taskIds = (active.tasks || []).map(task => task && task.id).filter(Boolean)
        const source = active.inputContext && active.inputContext.floorPlanImages ? active.inputContext.floorPlanImages[0] : null
        this.currentBatchId = active.id
        this.currentTaskIds = taskIds
        if (!this.previewUrl && source && source.url) this.previewUrl = source.url
        if (taskIds.length) this.continuePolling(true)
        else this.schedulePendingTaskRefresh()
      } catch (error) {
        this.errorMessage = error.message || '暂时无法加载线稿出方案记录'
        this.errorSuggestion = '请检查网络后刷新页面，已提交的任务不会丢失。'
      } finally {
        this.historyLoading = false
      }
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadPendingTask()
    },
    schedulePendingTaskRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadPendingTask()
      }, 2500)
    },
    async continuePolling(silent = false) {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.progress = Math.max(18, this.progress)
      this.generationText = '正在恢复产品方案任务'
      try {
        this.resultUrl = await this.pollTask(this.currentTaskIds[0])
        this.progress = 100
        this.generationText = '产品方案已生成'
        if (!silent) this.showMessage('线稿出方案任务已完成', 'success')
      } catch (error) {
        this.errorMessage = error.message || '查询产品方案任务失败'
        this.errorSuggestion = error.suggestion || '请稍后继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (!silent) this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    restoreHistoryRecord(record) {
      if (record.source) {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = record.source
      }
      this.resultUrl = record.result || ''
      this.currentBatchId = record.id
      this.currentTaskIds = record.taskIds || []
      this.errorMessage = ''
      this.errorSuggestion = ''
      if (record.status === 'generating' && this.currentTaskIds.length) this.continuePolling(true)
    },
    previewImage(src, title) {
      if (!src) return
      this.$emit('preview-image', src, { title })
    },
    previewHistoryImage(record, type) {
      const selected = type === 'source' ? record.source : record.result
      if (!selected) return
      const items = [
        record.source ? { src: record.source, title: '线稿出方案记录 · 手绘线稿' } : null,
        record.result ? { src: record.result, title: '线稿出方案记录 · 产品方案' } : null
      ].filter(Boolean)
      this.$emit('preview-image', selected, {
        title: type === 'source' ? '线稿出方案记录 · 手绘线稿' : '线稿出方案记录 · 产品方案',
        items,
        index: items.findIndex(item => item.src === selected)
      })
    },
    async downloadHistoryResult(record) {
      const previous = this.resultUrl
      this.resultUrl = record.result
      try {
        await this.downloadResult()
      } finally {
        this.resultUrl = previous
      }
    },
    async downloadResult() {
      try {
        const blob = await downloadResource(this.resultUrl)
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `sketch-product-plan-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showMessage('生成提示词已复制', 'success')
      } catch (error) {
        this.showMessage('复制失败，请手动选择提示词', 'warning')
      }
    },
    statusText(status) {
      return { generating: '生成中', completed: '已完成', failed: '生成失败' }[status] || '等待处理'
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
      }).format(date)
    },
    showMessage(message, type = 'info') {
      this.$message({ message, type, showClose: true, duration: 2600, offset: 24 })
    }
  }
}
</script>

<style scoped>
.sketch-plan-workspace{width:calc(100% - 68px);max-width:none;margin:26px auto 56px;color:#202b25}.workspace-intro{display:flex;align-items:center;justify-content:space-between;gap:28px;margin-bottom:18px;border:1px solid #e1e7e3;border-radius:10px;padding:20px 22px;background:linear-gradient(120deg,#fff 0%,#f7f9f8 58%,#eef4ff 100%)}.workspace-kicker{color:#2563eb;font-size:10px;font-weight:800;letter-spacing:1.8px}.workspace-intro h2,.method-panel h2,.history-panel h2{margin:7px 0 5px;font-size:21px;line-height:1.35}.workspace-intro p,.history-head p{margin:0;color:#718078;font-size:12px;line-height:1.6}.intro-badge{min-width:190px;display:grid;grid-template-columns:40px 1fr;align-items:center;gap:2px 10px;border:1px solid #d7e2fb;border-radius:8px;padding:12px 15px;background:rgba(255,255,255,.78)}.intro-badge i{grid-row:1/span 2;width:40px;height:40px;display:grid;place-items:center;border-radius:8px;background:#2563eb;color:#fff;font-size:18px}.intro-badge span{color:#7a8780;font-size:10px}.intro-badge strong{font-size:12px}.sketch-plan-main{min-height:690px;display:grid;grid-template-columns:360px minmax(0,1fr);overflow:hidden;border:1px solid #dde3de;border-radius:10px;background:#fff;box-shadow:0 12px 34px rgba(35,48,41,.07)}.sketch-plan-settings{min-width:0;border-right:1px solid #e3e7e4;padding:22px;background:#fafbfa}.setting-section+.setting-section{margin-top:24px;padding-top:22px;border-top:1px solid #e3e7e4}.section-title{display:flex;gap:11px;margin-bottom:13px}.section-title>span{width:25px;height:25px;display:grid;flex:0 0 25px;place-items:center;border-radius:50%;background:#eaf0ff;color:#2563eb;font-size:10px;font-weight:800}.section-title div{display:flex;flex-direction:column;gap:3px}.section-title strong{font-size:13px}.section-title small{color:#819087;font-size:10px;line-height:1.45}.sketch-dropzone{height:154px;overflow:hidden;display:grid;position:relative;border:1.5px dashed #b8c4bc;border-radius:8px;background:#fff;cursor:pointer;transition:border-color .2s,background .2s}.sketch-dropzone:hover{border-color:#5d87df;background:#f8faff}.sketch-dropzone input{width:1px;height:1px;position:absolute;opacity:0}.sketch-dropzone>img{width:100%;height:100%;object-fit:contain;background:#fff}.dropzone-empty{display:flex;align-items:center;justify-content:center;flex-direction:column;padding:18px;text-align:center}.dropzone-empty i{margin-bottom:7px;color:#4677da;font-size:24px}.dropzone-empty strong{font-size:12px}.dropzone-empty small{margin-top:5px;color:#8a968f;font-size:9px}.replace-sketch{position:absolute;right:8px;bottom:8px;border:0;border-radius:5px;padding:6px 10px;background:rgba(20,31,25,.78);color:#fff;font-size:10px;cursor:pointer}.file-rule{display:flex;align-items:flex-start;gap:7px;margin-top:8px;color:#8a6c36;font-size:9px;line-height:1.45}.file-rule.valid{color:#2c7b55}.file-rule i{margin-top:1px}.preset-row{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px}.preset-row button{min-height:30px;border:1px solid #dce3de;border-radius:999px;padding:0 10px;background:#fff;color:#5c6a62;font-size:10px;cursor:pointer;transition:.2s}.preset-row button:hover,.preset-row button.active{border-color:#86a6e9;background:#eff4ff;color:#245ec5}.field-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.field-grid label,.requirement-field{display:flex;flex-direction:column;gap:6px}.field-grid label>span,.requirement-field>span{color:#56645c;font-size:10px;font-weight:700}.field-grid .el-select{width:100%}.requirement-field{margin-top:11px}.requirement-field>span{display:flex;justify-content:space-between}.requirement-field em{color:#96a099;font-size:9px;font-style:normal;font-weight:400}.requirement-field textarea{min-height:92px;resize:vertical;border:1px solid #dce2de;border-radius:6px;padding:10px 11px;background:#fff;color:#27332d;font:inherit;font-size:11px;line-height:1.65;outline:none}.requirement-field textarea:focus{border-color:#6f96e7;box-shadow:0 0 0 3px rgba(37,99,235,.09)}.output-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:11px}.output-row>span{flex:0 0 auto;color:#56645c;font-size:10px;font-weight:700}.ratio-row{align-items:flex-start}.ratio-row .el-radio-group{display:flex;flex-wrap:wrap;justify-content:flex-end}.generate-button{width:100%;min-height:52px;display:grid;grid-template-columns:24px 1fr;align-items:center;margin-top:24px;border:0;border-radius:7px;padding:8px 14px;background:#2563eb;color:#fff;box-shadow:0 8px 20px rgba(37,99,235,.22);cursor:pointer}.generate-button:hover{background:#1d4ed8}.generate-button:focus-visible{outline:3px solid rgba(37,99,235,.25);outline-offset:2px}.generate-button:disabled{opacity:.7;cursor:wait}.generate-button>i,.button-spinner{grid-row:1/span 2;font-size:18px}.generate-button>span:not(.button-spinner){text-align:left;font-size:12px;font-weight:800}.generate-button small{text-align:left;color:rgba(255,255,255,.75);font-size:9px}.button-spinner{width:17px;height:17px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:spin .8s linear infinite}.generation-progress{height:4px;overflow:hidden;margin-top:7px;border-radius:2px;background:#dfe7e2}.generation-progress i{height:100%;display:block;background:#2563eb;transition:width .35s ease}.result-panel{min-width:0;display:flex;flex-direction:column;padding:22px}.result-head,.history-head,.method-panel>header{display:flex;align-items:center;justify-content:space-between;gap:20px}.result-head>div:first-child{display:flex;flex-direction:column;gap:4px}.result-head strong{font-size:13px}.result-head span{color:#849088;font-size:10px}.result-actions,.history-actions{display:flex;gap:7px}.result-actions button,.history-actions button,.history-refresh,.prompt-toggle{min-width:42px;height:42px;border:1px solid #dce3de;border-radius:6px;background:#fff;color:#536159;cursor:pointer}.result-actions button:hover,.history-actions button:hover,.history-refresh:hover,.prompt-toggle:hover{border-color:#86a7ea;color:#2563eb}.result-stage{min-height:520px;display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px}.result-viewport{min-width:0;overflow:hidden;display:flex;position:relative;border:1px solid #e0e5e1;border-radius:8px;background:#f4f6f5}.viewport-label{position:absolute;z-index:2;top:10px;left:10px;border-radius:4px;padding:5px 8px;background:rgba(24,34,28,.8);color:#fff;font-size:9px;font-weight:700}.image-preview-button{width:100%;height:100%;min-height:520px;display:grid;place-items:center;border:0;padding:18px;background:#f7f8f7;cursor:zoom-in}.image-preview-button img{width:100%;height:100%;max-height:550px;display:block;object-fit:contain}.image-preview-button>i{position:absolute;right:12px;bottom:12px;width:34px;height:34px;display:grid;place-items:center;border-radius:50%;background:rgba(24,34,28,.78);color:#fff;opacity:0;transition:opacity .2s}.image-preview-button:hover>i,.image-preview-button:focus-visible>i{opacity:1}.plan-viewport,.plan-viewport .image-preview-button{background:#fff}.viewport-empty{width:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;padding:24px;color:#7c8981;text-align:center}.viewport-empty>i{margin-bottom:10px;color:#4b79da;font-size:30px}.viewport-empty strong{color:#39453f;font-size:13px}.viewport-empty span{margin-top:6px;font-size:10px}.model-loader{display:flex;gap:5px}.model-loader i{width:7px;height:7px;border-radius:50%;background:#2563eb;animation:pulse 1s infinite}.model-loader i:nth-child(2){animation-delay:.15s}.model-loader i:nth-child(3){animation-delay:.3s}.quality-strip{display:flex;flex-wrap:wrap;gap:8px 15px;margin-top:14px;border-radius:7px;padding:11px 13px;background:#f5f8f6;color:#59675f;font-size:9px}.quality-strip i{margin-right:4px;color:#23825c}.generation-error{display:flex;align-items:center;gap:11px;margin-top:13px;border:1px solid #f2c8c4;border-radius:7px;padding:11px 13px;background:#fff7f6;color:#a23d34}.generation-error>i{font-size:20px}.generation-error>div{min-width:0;display:flex;flex:1;flex-direction:column;gap:3px}.generation-error strong{font-size:11px}.generation-error span{font-size:9px}.generation-error button{border:1px solid #d9968f;border-radius:5px;padding:7px 10px;background:#fff;color:#a23d34;cursor:pointer}.method-panel,.history-panel{margin-top:22px;border:1px solid #dde3de;border-radius:10px;padding:22px;background:#fff;box-shadow:0 10px 28px rgba(35,48,41,.05)}.prompt-toggle,.history-refresh{min-width:auto;display:flex;align-items:center;gap:7px;padding:0 13px;font-size:10px}.method-steps{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:16px}.method-steps article{min-width:0;display:grid;grid-template-columns:26px 32px 1fr;align-items:start;gap:9px;border:1px solid #e3e8e4;border-radius:7px;padding:13px;background:#fafbfa}.method-steps article>span{color:#9aa49e;font-size:9px;font-weight:800}.method-steps article>i{width:32px;height:32px;display:grid;place-items:center;border-radius:7px;background:#eaf0ff;color:#2563eb}.method-steps strong{font-size:11px}.method-steps p{margin:4px 0 0;color:#7b8880;font-size:9px;line-height:1.55}.prompt-content{display:grid;grid-template-columns:1fr auto;gap:8px;margin-top:14px;border:1px solid #dce4df;border-radius:7px;padding:14px;background:#f8faf9}.prompt-content>div{display:flex;flex-direction:column;gap:3px}.prompt-content>div span{color:#2563eb;font-size:8px;font-weight:800;letter-spacing:1.2px}.prompt-content>div strong{font-size:11px}.prompt-content>button{width:36px;height:36px;border:1px solid #d9e1dc;border-radius:5px;background:#fff;color:#536159;cursor:pointer}.prompt-content pre{grid-column:1/-1;max-height:270px;overflow:auto;margin:4px 0 0;white-space:pre-wrap;color:#536159;font-family:inherit;font-size:10px;line-height:1.7}.history-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-top:16px}.history-grid>article{min-width:0;overflow:hidden;border:1px solid #e0e5e1;border-radius:8px;background:#fff}.history-grid>article.active{border-color:#82a4e8;box-shadow:0 8px 22px rgba(37,99,235,.1)}.history-images{height:190px;display:grid;grid-template-columns:1fr 1fr;gap:1px;position:relative;background:#e3e8e4}.history-images button,.history-images>div{min-width:0;overflow:hidden;display:flex;align-items:center;justify-content:center;flex-direction:column;position:relative;border:0;padding:0;background:#f4f6f5;color:#809087;cursor:zoom-in}.history-images img{width:100%;height:100%;object-fit:contain;background:#fff}.history-images small{position:absolute;right:5px;bottom:5px;border-radius:4px;padding:3px 5px;background:rgba(25,35,29,.72);color:#fff;font-size:8px}.history-status{position:absolute;z-index:2;top:7px;left:7px;border-radius:999px;padding:4px 7px;background:#eef1ef;color:#657169;font-size:8px}.history-status.completed{background:#e2f5eb;color:#24714f}.history-status.generating{background:#e9f0ff;color:#285fbf}.history-status.failed{background:#fde9e7;color:#a54037}.history-grid footer{min-height:70px;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px 12px}.history-grid footer>div:first-child{min-width:0;display:flex;flex-direction:column;gap:4px}.history-grid footer strong{overflow:hidden;font-size:10px;text-overflow:ellipsis;white-space:nowrap}.history-grid footer small{color:#88948d;font-size:8px}.history-actions button{width:38px;height:38px;min-width:38px}.history-empty{min-height:170px;display:flex;align-items:center;justify-content:center;flex-direction:column;color:#8a968f;text-align:center}.history-empty>i{margin-bottom:9px;color:#6c91df;font-size:25px}.history-empty strong{color:#4d5b53;font-size:12px}.history-empty span{margin-top:5px;font-size:9px}.prompt-reveal-enter-active,.prompt-reveal-leave-active{transition:opacity .2s ease,transform .2s ease}.prompt-reveal-enter,.prompt-reveal-leave-to{opacity:0;transform:translateY(-5px)}@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:.35;transform:translateY(0)}50%{opacity:1;transform:translateY(-3px)}}@media(max-width:1300px){.sketch-plan-main{grid-template-columns:330px minmax(0,1fr)}.method-steps{grid-template-columns:repeat(2,1fr)}.history-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:980px){.sketch-plan-workspace{width:calc(100% - 24px);margin-top:16px}.workspace-intro{align-items:flex-start}.intro-badge{display:none}.sketch-plan-main{grid-template-columns:1fr}.sketch-plan-settings{border-right:0;border-bottom:1px solid #e3e7e4}.result-stage{min-height:440px}.image-preview-button{min-height:440px}}@media(max-width:700px){.workspace-intro,.sketch-plan-settings,.result-panel,.method-panel,.history-panel{padding:14px}.workspace-intro h2,.method-panel h2,.history-panel h2{font-size:18px}.field-grid,.result-stage,.method-steps,.history-grid{grid-template-columns:1fr}.result-stage{min-height:auto}.image-preview-button{min-height:360px}.result-head,.history-head,.method-panel>header{align-items:flex-start;flex-direction:column}.quality-strip{gap:7px 10px}.history-images{height:240px}}@media(prefers-reduced-motion:reduce){.button-spinner,.model-loader i{animation:none}.generation-progress i,.prompt-reveal-enter-active,.prompt-reveal-leave-active{transition:none}}
</style>
